import React, { useEffect } from 'react';
import { useCareFlow360Store } from './hooks/useCareFlow360Store';
import {
  careflow360Api,
  CareflowAuditEntry,
  CareflowCollaborationAuditEntry,
  CareflowCollaborationNote,
} from './api/careflow360Api';
import ApexoToothChart from './chart/ApexoToothChart';
import ClinicNotesBoard from '../common/apexo/ClinicNotesBoard';
import { ChartEvent } from './chart/types';
import { useI18n } from '../../i18n/I18nProvider';
import { apiGet, apiPatch, apiPost } from '../../services/api';
import { emitCareflowSync } from '../../services/careflowSync';
import { getRole } from '../../services/auth';
import './styles/careflow360.css';

// New Modular Components
import CareFlow360Header from './CareFlow360Header';
import CareFlow360Timeline from './CareFlow360Timeline';
import CareFlow360Collaboration from './CareFlow360Collaboration';

// New Clinical Components
import DmftBadge from '../doctor/clinical/DmftBadge';
import MedicalHistoryForm from '../careflow/MedicalHistoryForm';
import PhotoGallery from '../doctor/clinical/PhotoGallery';
import PerioChart from '../doctor/clinical/PerioChart';
import ConsentForm from '../doctor/ConsentForm';
import PaymentBadge from '../common/PaymentBadge';

interface CareFlow360WorkspaceProps {
  visitId: number | null;
  patientId: string;
  patientName?: string;
  onClosePanel?: () => void;
  onOpenBilling?: () => void;
  onOpenDiagnosis?: () => void;
  onOpenTreatment?: () => void;
  onOpenPharmacy?: () => void;
}

type TagsSyncState = 'idle' | 'saving' | 'conflict' | 'error';

type PatientDetails = {
  patient_id: string;
  name?: string;
  age?: string | number;
  gender?: string;
  mobile?: string;
  phone?: string;
  area?: string;
  reference_source?: string;
  medical_alerts?: string;
};

type VisitItem = {
  visit_id: number;
  visit_date?: string;
  visit_type?: string;
  status?: string;
  remarks?: string;
  appointment_time?: string | null;
  created_at?: string;
};

function parseMentions(input: string): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const token of input.split(',')) {
    const cleaned = token.trim().replace(/\s+/g, ' ').slice(0, 64);
    if (!cleaned) continue;
    const key = cleaned.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(cleaned);
    if (out.length >= 25) break;
  }
  return out;
}

export default function CareFlow360Workspace({
  visitId,
  patientId,
  patientName,
  onClosePanel,
  onOpenBilling,
  onOpenDiagnosis,
  onOpenTreatment,
  onOpenPharmacy,
}: CareFlow360WorkspaceProps) {
  const { formatDate } = useI18n();
  const { events, setEvents, visitVersion, setVisitVersion, pendingSaves, pendingDeletes, clearPendingSaves } = useCareFlow360Store();
  const [visitTags, setVisitTags] = React.useState<string[]>([]);
  const [auditTrail, setAuditTrail] = React.useState<CareflowAuditEntry[]>([]);
  const [tagsUpdatedAt, setTagsUpdatedAt] = React.useState<string | null>(null);
  const [tagsSyncState, setTagsSyncState] = React.useState<TagsSyncState>('idle');
  const [streamUpdatedAt, setStreamUpdatedAt] = React.useState<string | null>(null);
  const [liveStatus, setLiveStatus] = React.useState<
    'connecting' | 'open' | 'closed' | 'error' | 'unauthenticated'
  >('closed');
  const lastSyncedTagsRef = React.useRef<string>('[]');
  const tagSaveNonceRef = React.useRef(0);
  const liveReconnectTimerRef = React.useRef<number | null>(null);
  const liveRefreshTimerRef = React.useRef<number | null>(null);

  const [historyTooth, setHistoryTooth] = React.useState('');
  const [historyLabel, setHistoryLabel] = React.useState('');
  const [historyNote, setHistoryNote] = React.useState('');
  const [historySaving, setHistorySaving] = React.useState(false);

  const [collabNotes, setCollabNotes] = React.useState<CareflowCollaborationNote[]>([]);
  const [collabAudit, setCollabAudit] = React.useState<CareflowCollaborationAuditEntry[]>([]);
  const [newCollabBody, setNewCollabBody] = React.useState('');
  const [newCollabMentions, setNewCollabMentions] = React.useState('');
  const [collabSaving, setCollabSaving] = React.useState(false);
  const [viewMode, setViewMode] = React.useState<'timeline' | 'kanban'>('timeline');
  const [rightTab, setRightTab] = React.useState<
    'dental-notes' | 'timeline' | 'kanban' | 'patient-details' | 'appointments' | 'billing'
  >('dental-notes');
  const [patientDetails, setPatientDetails] = React.useState<PatientDetails | null>(null);
  const [patientVisits, setPatientVisits] = React.useState<VisitItem[]>([]);
  const [detailsSaving, setDetailsSaving] = React.useState(false);
  const [appointmentSaving, setAppointmentSaving] = React.useState(false);
  const [appointmentDate, setAppointmentDate] = React.useState<string>(() =>
    new Date().toISOString().slice(0, 10)
  );
  const [appointmentTime, setAppointmentTime] = React.useState('');
  const [appointmentRemarks, setAppointmentRemarks] = React.useState('Careflow follow-up');
  const [billingSnapshot, setBillingSnapshot] = React.useState({
    treatmentItems: 0,
    estAmount: 0,
    consultantAmount: 0,
    pendingLabel: 'Open billing',
  });
  const [dashboardStats, setDashboardStats] = React.useState({
    appointmentsToday: 0,
    newPatientsToday: 0,
    paymentsToday: 0,
  });

  // Clinical tool modals
  const [showMedicalHistory, setShowMedicalHistory] = React.useState(false);
  const [showPerioChart, setShowPerioChart] = React.useState(false);
  const [showConsentForm, setShowConsentForm] = React.useState(false);

  const normalizeTags = React.useCallback((rawTags: string[]) => {
    const out: string[] = [];
    const seen = new Set<string>();
    for (const raw of rawTags) {
      const cleaned = String(raw || '').trim().replace(/\s+/g, ' ').slice(0, 40);
      if (!cleaned) continue;
      const key = cleaned.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(cleaned);
      if (out.length >= 50) break;
    }
    return out;
  }, []);

  const formatDateTime = React.useCallback(
    (iso?: string | null) => {
      if (!iso) return '—';
      return formatDate(new Date(iso), {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    },
    [formatDate]
  );

  const refreshWorkspace = React.useCallback(async () => {
    if (!visitId) {
      setEvents([]);
      setVisitVersion(0);
      setAuditTrail([]);
      setVisitTags([]);
      setTagsUpdatedAt(null);
      setStreamUpdatedAt(null);
      setCollabNotes([]);
      setCollabAudit([]);
      lastSyncedTagsRef.current = '[]';
      setTagsSyncState('idle');
      return;
    }

    const [bundle, audit, tagsResponse, collaboration] = await Promise.all([
      careflow360Api.fetchChartEvents(visitId, patientId),
      careflow360Api.fetchAuditTrail(visitId, 50),
      careflow360Api.fetchVisitTags(visitId),
      careflow360Api.fetchCollaboration(visitId, true),
    ]);
    const normalizedTags = normalizeTags(tagsResponse.tags || []);
    setEvents(bundle.events);
    setVisitVersion(bundle.visitVersion);
    setStreamUpdatedAt(bundle.streamUpdatedAt);
    setAuditTrail(audit);
    setVisitTags(normalizedTags);
    lastSyncedTagsRef.current = JSON.stringify(normalizedTags);
    setTagsUpdatedAt(tagsResponse.updated_at || null);
    setTagsSyncState('idle');
    setCollabNotes(collaboration.notes);
    setCollabAudit(collaboration.audit);
  }, [normalizeTags, patientId, setEvents, setVisitVersion, visitId]);

  useEffect(() => {
    let cancelled = false;
    refreshWorkspace().catch(() => {
      if (cancelled) return;
      setEvents([]);
      setVisitVersion(0);
      setAuditTrail([]);
      setVisitTags([]);
      setTagsUpdatedAt(null);
      setStreamUpdatedAt(null);
      setCollabNotes([]);
      setCollabAudit([]);
      lastSyncedTagsRef.current = '[]';
      setTagsSyncState('error');
    });
    return () => {
      cancelled = true;
    };
  }, [refreshWorkspace, setEvents, setVisitVersion]);

  useEffect(() => {
    if (liveReconnectTimerRef.current != null) {
      window.clearTimeout(liveReconnectTimerRef.current);
      liveReconnectTimerRef.current = null;
    }
    if (liveRefreshTimerRef.current != null) {
      window.clearTimeout(liveRefreshTimerRef.current);
      liveRefreshTimerRef.current = null;
    }
    if (!visitId) {
      setLiveStatus('closed');
      return;
    }

    let disposed = false;
    let subscription: { close: () => void } | null = null;

    const scheduleRefresh = () => {
      if (liveRefreshTimerRef.current != null) return;
      liveRefreshTimerRef.current = window.setTimeout(() => {
        liveRefreshTimerRef.current = null;
        if (!disposed) {
          void refreshWorkspace();
        }
      }, 250);
    };

    const connect = () => {
      if (disposed) return;
      subscription?.close();
      subscription = careflow360Api.subscribeLive(visitId, {
        onStatus: (status) => {
          if (disposed) return;
          setLiveStatus(status);
          if (status === 'closed' || status === 'error') {
            if (liveReconnectTimerRef.current != null) {
              window.clearTimeout(liveReconnectTimerRef.current);
            }
            liveReconnectTimerRef.current = window.setTimeout(() => {
              liveReconnectTimerRef.current = null;
              connect();
            }, 1800);
          }
        },
        onMessage: (event) => {
          if (disposed) return;
          if (event.stream_version != null && Number.isFinite(Number(event.stream_version))) {
            setVisitVersion(Number(event.stream_version));
          }
          if (event.event === 'pong' || event.event === 'connected') {
            return;
          }
          scheduleRefresh();
        },
      });
    };

    connect();
    return () => {
      disposed = true;
      subscription?.close();
      if (liveReconnectTimerRef.current != null) {
        window.clearTimeout(liveReconnectTimerRef.current);
        liveReconnectTimerRef.current = null;
      }
      if (liveRefreshTimerRef.current != null) {
        window.clearTimeout(liveRefreshTimerRef.current);
        liveRefreshTimerRef.current = null;
      }
    };
  }, [refreshWorkspace, setVisitVersion, visitId]);

  useEffect(() => {
    let cancelled = false;
    if (!visitId) {
      setAuditTrail([]);
      return () => {
        cancelled = true;
      };
    }
    careflow360Api.fetchAuditTrail(visitId, 50).then((audit) => {
      if (!cancelled) setAuditTrail(audit);
    });
    return () => {
      cancelled = true;
    };
  }, [visitId, events.length, visitVersion]);

  useEffect(() => {
    if (viewMode === 'timeline' && rightTab === 'kanban') setRightTab('timeline');
    if (viewMode === 'kanban' && rightTab === 'timeline') setRightTab('kanban');
  }, [rightTab, viewMode]);

  useEffect(() => {
    let cancelled = false;
    const today = new Date().toISOString().slice(0, 10);
    const role = (getRole() || '').toLowerCase();
    const canReadAdminSummary = role === 'admin';

    const loadDashboardStats = async () => {
      try {
        const visitsRes = (await apiGet(`/api/visits-by-date?date=${today}`)) as {
          visits?: Array<unknown>;
        };
        const appointmentsToday = Array.isArray(visitsRes?.visits) ? visitsRes.visits.length : 0;

        // Try admin summary for richer metrics only when role is admin.
        let newPatientsToday = 0;
        let paymentsToday = 0;
        if (canReadAdminSummary) {
          try {
            const summary = (await apiGet(
              `/api/admin/dashboard/summary?date=${today}`
            )) as { total_bills?: number; revenue_total?: number };
            newPatientsToday = Number(summary?.total_bills || 0);
            paymentsToday = Number(summary?.revenue_total || 0);
          } catch {
            // Keep fallback values.
          }
        }

        if (!cancelled) {
          setDashboardStats({ appointmentsToday, newPatientsToday, paymentsToday });
        }
      } catch {
        if (!cancelled) {
          setDashboardStats({ appointmentsToday: 0, newPatientsToday: 0, paymentsToday: 0 });
        }
      }
    };

    void loadDashboardStats();
    return () => {
      cancelled = true;
    };
  }, [events.length]);

  useEffect(() => {
    let cancelled = false;
    if (!patientId) {
      setPatientDetails(null);
      setPatientVisits([]);
      return () => {
        cancelled = true;
      };
    }

    const loadPatientContext = async () => {
      try {
        const [patientRes, visitsRes] = await Promise.all([
          apiGet(`/api/patients/${encodeURIComponent(patientId)}`),
          apiGet(`/api/visits/patient/${encodeURIComponent(patientId)}`),
        ]);
        if (cancelled) return;

        const patient = (patientRes || {}) as PatientDetails;
        const visits = (Array.isArray(visitsRes) ? visitsRes : []) as VisitItem[];
        visits.sort((a, b) => {
          const aTs = Date.parse(String(a.visit_date || a.created_at || '')) || 0;
          const bTs = Date.parse(String(b.visit_date || b.created_at || '')) || 0;
          return bTs - aTs;
        });
        setPatientDetails(patient);
        setPatientVisits(visits);
      } catch {
        if (!cancelled) {
          setPatientDetails(null);
          setPatientVisits([]);
        }
      }
    };

    void loadPatientContext();
    return () => {
      cancelled = true;
    };
  }, [patientId, events.length]);

  useEffect(() => {
    let cancelled = false;
    if (!visitId) {
      setBillingSnapshot({
        treatmentItems: 0,
        estAmount: 0,
        consultantAmount: 0,
        pendingLabel: 'No active visit',
      });
      return () => {
        cancelled = true;
      };
    }

    const loadBillingSnapshot = async () => {
      try {
        const rows = (await apiGet(`/api/doctor/procedures/by-visit/${visitId}`)) as Array<
          Record<string, unknown>
        >;
        if (cancelled) return;
        const list = Array.isArray(rows) ? rows : [];
        const consultantAmount = list.reduce(
          (sum, row) => sum + Number(row.consultant_fee || 0),
          0
        );
        const estAmount = list.reduce((sum, row) => {
          const base =
            Number(row.final_price || 0) ||
            Number(row.base_price || 0) ||
            Number(row.price || 0) ||
            Number(row.amount || 0) ||
            Number(row.cost || 0);
          return sum + base;
        }, 0);
        setBillingSnapshot({
          treatmentItems: list.length,
          estAmount,
          consultantAmount,
          pendingLabel: list.length > 0 ? 'Ready for billing' : 'No treatments yet',
        });
      } catch {
        if (!cancelled) {
          setBillingSnapshot({
            treatmentItems: 0,
            estAmount: 0,
            consultantAmount: 0,
            pendingLabel: 'Billing data unavailable',
          });
        }
      }
    };

    void loadBillingSnapshot();
    return () => {
      cancelled = true;
    };
  }, [visitId, events.length]);

  useEffect(() => {
    if (!visitId) return;
    const normalized = normalizeTags(visitTags);
    const normalizedSerialized = JSON.stringify(normalized);

    if (normalizedSerialized !== JSON.stringify(visitTags)) {
      setVisitTags(normalized);
      return;
    }
    if (normalizedSerialized === lastSyncedTagsRef.current) return;

    const nonce = ++tagSaveNonceRef.current;
    setTagsSyncState('saving');
    const timer = window.setTimeout(async () => {
      try {
        const saved = await careflow360Api.saveVisitTags(visitId, normalized, tagsUpdatedAt || undefined);
        if (nonce !== tagSaveNonceRef.current) return;
        const savedTags = normalizeTags(saved.tags || []);
        lastSyncedTagsRef.current = JSON.stringify(savedTags);
        setVisitTags(savedTags);
        setTagsUpdatedAt(saved.updated_at || null);
        setTagsSyncState('idle');
        emitCareflowSync({ reason: 'tags_saved', visitId, patientId });
      } catch (error) {
        if (nonce !== tagSaveNonceRef.current) return;
        const err = error as Error & { status?: number };
        if (err?.status === 409) {
          try {
            const latest = await careflow360Api.fetchVisitTags(visitId);
            const merged = normalizeTags([...(latest.tags || []), ...normalized]);
            const resolved = await careflow360Api.saveVisitTags(
              visitId,
              merged,
              latest.updated_at || undefined
            );
            if (nonce !== tagSaveNonceRef.current) return;
            const resolvedTags = normalizeTags(resolved.tags || merged);
            lastSyncedTagsRef.current = JSON.stringify(resolvedTags);
            setVisitTags(resolvedTags);
            setTagsUpdatedAt(resolved.updated_at || latest.updated_at || null);
            setTagsSyncState('conflict');
            emitCareflowSync({ reason: 'tags_saved', visitId, patientId });
            window.setTimeout(() => {
              if (nonce === tagSaveNonceRef.current) setTagsSyncState('idle');
            }, 1200);
          } catch {
            setTagsSyncState('error');
          }
        } else {
          setTagsSyncState('error');
        }
      }
    }, 500);

    return () => {
      window.clearTimeout(timer);
    };
  }, [visitId, visitTags, tagsUpdatedAt, normalizeTags, patientId]);

  const currentTreatmentEvents = React.useMemo(
    () =>
      events
        .filter((event) => !event.isHistorical && (event.domain || 'treatment') === 'treatment')
        .sort((a, b) => b.timestamp - a.timestamp),
    [events]
  );
  const currentHistoryEvents = React.useMemo(
    () =>
      events
        .filter((event) => !event.isHistorical && event.domain === 'history')
        .sort((a, b) => b.timestamp - a.timestamp),
    [events]
  );
  const historicalEvents = React.useMemo(
    () => events.filter((event) => event.isHistorical).sort((a, b) => b.timestamp - a.timestamp),
    [events]
  );

  const handleAddHistory = async () => {
    if (!visitId || historySaving) return;
    const tooth = historyTooth.trim();
    const label = historyLabel.trim();
    if (!tooth || !label) return;

    setHistorySaving(true);
    try {
      const result = await careflow360Api.createToothHistory(visitId, {
        tooth_number: tooth,
        label,
        note: historyNote.trim(),
        expected_version: visitVersion,
      });
      if (result.visitVersion != null && Number.isFinite(result.visitVersion)) {
        setVisitVersion(result.visitVersion);
      }
      await refreshWorkspace();
      emitCareflowSync({ reason: 'history_created', visitId, patientId });
      setHistoryTooth('');
      setHistoryLabel('');
      setHistoryNote('');
    } catch (error) {
      const err = error as Error & { status?: number };
      if (err?.status === 409) {
        await refreshWorkspace();
      }
    } finally {
      setHistorySaving(false);
    }
  };

  const handleDeleteHistory = async (event: ChartEvent) => {
    if (!visitId) return;
    const historyId = Number(event.persistedId ?? event.id);
    if (!Number.isFinite(historyId) || historyId <= 0) return;
    try {
      const result = await careflow360Api.deleteToothHistory(historyId, visitVersion);
      if (result.visitVersion != null && Number.isFinite(result.visitVersion)) {
        setVisitVersion(result.visitVersion);
      }
      await refreshWorkspace();
      emitCareflowSync({ reason: 'history_deleted', visitId, patientId });
    } catch (error) {
      const err = error as Error & { status?: number };
      if (err?.status === 409) {
        await refreshWorkspace();
      }
    }
  };

  const handleCreateCollaborationNote = async () => {
    if (!visitId || collabSaving) return;
    const body = newCollabBody.trim();
    if (!body) return;
    setCollabSaving(true);
    try {
      const note = await careflow360Api.createCollaborationNote(visitId, {
        body,
        mentions: parseMentions(newCollabMentions),
        attachment_ids: [],
      });
      setCollabNotes((prev) => [note, ...prev.filter((item) => item.id !== note.id)]);
      setNewCollabBody('');
      setNewCollabMentions('');
      const latest = await careflow360Api.fetchCollaboration(visitId, true);
      setCollabNotes(latest.notes);
      setCollabAudit(latest.audit);
      emitCareflowSync({ reason: 'collab_created', visitId, patientId });
    } catch (error) {
      console.error('Failed to create collaboration note', error);
    } finally {
      setCollabSaving(false);
    }
  };

  const handleDeleteCollaborationNote = async (noteId: number) => {
    if (!visitId) return;
    try {
      await careflow360Api.deleteCollaborationNote(noteId);
      setCollabNotes((prev) => prev.filter((item) => item.id !== noteId));
      const latest = await careflow360Api.fetchCollaboration(visitId, true);
      setCollabNotes(latest.notes);
      setCollabAudit(latest.audit);
      emitCareflowSync({ reason: 'collab_deleted', visitId, patientId });
    } catch (error) {
      console.error('Failed to delete collaboration note', error);
    }
  };

  const handleManualSave = async () => {
    if (!visitId || !patientId) return;

    let currentVersion = visitVersion;

    try {
      if (pendingDeletes.length > 0 || pendingSaves.length > 0) {
        // Process deletes
        for (const id of pendingDeletes) {
          try {
            const res = await careflow360Api.deleteChartEvent(id, currentVersion);
            if (res.visitVersion != null && res.visitVersion >= 0) {
              currentVersion = res.visitVersion;
            }
          } catch (e) {
            console.error("Failed deleting event", id, e);
          }
        }

        // Process saves
        for (const ev of pendingSaves) {
          try {
            const res = await careflow360Api.saveChartEvent(visitId, patientId, ev, currentVersion);
            if (res.visitVersion != null && res.visitVersion >= 0) {
              currentVersion = res.visitVersion;
            }
          } catch (e) {
            console.error("Failed saving event", ev, e);
          }
        }
        
        clearPendingSaves();
        setVisitVersion(currentVersion);
      }
    } catch (e) {
      console.error("Manual save failed", e);
    }

    await refreshWorkspace();
    emitCareflowSync({ reason: 'manual_refresh', visitId, patientId });
  };

  const handleSavePatientDetails = async () => {
    if (!patientDetails?.patient_id || detailsSaving) return;
    setDetailsSaving(true);
    try {
      const payload = {
        name: patientDetails.name || '',
        age: patientDetails.age ? Number(patientDetails.age) : undefined,
        gender: patientDetails.gender || undefined,
        mobile: patientDetails.mobile || patientDetails.phone || undefined,
        area: patientDetails.area || undefined,
        reference_source: patientDetails.reference_source || undefined,
        medical_alerts: patientDetails.medical_alerts || undefined,
      };
      const updated = (await apiPatch(
        `/api/patients/${encodeURIComponent(String(patientDetails.patient_id))}`,
        payload
      )) as PatientDetails;
      setPatientDetails((prev) => ({ ...(prev || {}), ...(updated || payload) }));
      emitCareflowSync({ reason: 'patient_updated', visitId, patientId });
    } catch (error) {
      console.error('Failed to save patient details', error);
      window.alert('Failed to save patient details.');
    } finally {
      setDetailsSaving(false);
    }
  };

  const handleQuickSchedule = async () => {
    if (!patientId || appointmentSaving) return;
    if (!appointmentDate) {
      window.alert('Select an appointment date.');
      return;
    }
    setAppointmentSaving(true);
    try {
      await apiPost('/api/visits/', {
        patient_id: patientId,
        visit_date: appointmentDate,
        visit_type: 'Review',
        remarks: appointmentRemarks.trim() || 'Careflow follow-up',
        appointment_time: appointmentTime ? `${appointmentDate}T${appointmentTime}:00` : null,
        created_source: 'careflow_quick',
      });
      const visitsRes = (await apiGet(`/api/visits/patient/${encodeURIComponent(patientId)}`)) as VisitItem[];
      const visits = Array.isArray(visitsRes) ? visitsRes.slice() : [];
      visits.sort((a, b) => {
        const aTs = Date.parse(String(a.visit_date || a.created_at || '')) || 0;
        const bTs = Date.parse(String(b.visit_date || b.created_at || '')) || 0;
        return bTs - aTs;
      });
      setPatientVisits(visits);
      emitCareflowSync({ reason: 'appointment_created', visitId, patientId });
    } catch (error) {
      console.error('Failed to schedule appointment', error);
      window.alert('Failed to create appointment. If this date already exists, choose another date.');
    } finally {
      setAppointmentSaving(false);
    }
  };

  return (
    <div className="careflow360-workspace">
      <CareFlow360Header
        patientName={patientName}
        patientId={patientId ? String(patientId) : undefined}
        visitTags={visitTags}
        setVisitTags={setVisitTags}
        tagsSyncState={tagsSyncState}
        tagsUpdatedAt={tagsUpdatedAt}
        visitVersion={visitVersion}
        streamUpdatedAt={streamUpdatedAt}
        liveStatus={liveStatus}
        viewMode={viewMode}
        setViewMode={setViewMode}
        formatDateTime={formatDateTime}
        normalizeTags={normalizeTags}
        dashboardStats={dashboardStats}
      />

      <div className={`cf360-main-layout ${viewMode === 'kanban' ? 'kanban-view' : ''}`}>
        <div className={`cf360-center-section ${rightTab !== 'dental-notes' ? 'is-muted' : ''}`}>
          {viewMode === 'timeline' ? (
            <div className="cf360-sidebar-content-scroll">
              <CareFlow360Timeline
                currentTreatmentEvents={currentTreatmentEvents}
                currentHistoryEvents={currentHistoryEvents}
                historicalEvents={historicalEvents}
                auditTrail={auditTrail}
                historyTooth={historyTooth}
                setHistoryTooth={setHistoryTooth}
                historyLabel={historyLabel}
                setHistoryLabel={setHistoryLabel}
                historyNote={historyNote}
                setHistoryNote={setHistoryNote}
                historySaving={historySaving}
                handleAddHistory={handleAddHistory}
                handleDeleteHistory={handleDeleteHistory}
                formatDateTime={formatDateTime}
              />
              
              <div style={{ marginTop: '32px' }}>
                <CareFlow360Collaboration
                  collabNotes={collabNotes}
                  collabAudit={collabAudit}
                  newCollabBody={newCollabBody}
                  setNewCollabBody={setNewCollabBody}
                  newCollabMentions={newCollabMentions}
                  setNewCollabMentions={setNewCollabMentions}
                  collabSaving={collabSaving}
                  handleCreateCollaborationNote={handleCreateCollaborationNote}
                  handleDeleteCollaborationNote={handleDeleteCollaborationNote}
                  formatDateTime={formatDateTime}
                />
              </div>
            </div>
          ) : (
            <ClinicNotesBoard visitId={visitId} patientId={patientId} />
          )}
        </div>

        <div className="cf360-right-patient-window">
          <div className="cf360-right-window-header cf360-right-window-topline">
            <div className="cf360-patient-identity">
              <span className="cf360-patient-role">Patient</span>
              <strong>{patientName || `#${patientId}`}</strong>
            </div>
            <div className="cf360-window-icons">
              <span className="cf360-mini-icon" title="Panels">◫2</span>
              <button type="button" className="cf360-mini-btn" onClick={() => onClosePanel?.()}>✕</button>
            </div>
          </div>
          {/* DMFT + Payment badges + Clinical Tools */}
          {patientId && (
            <div style={{ display: 'flex', gap: 8, padding: '4px 12px', flexWrap: 'wrap', alignItems: 'center' }}>
              <DmftBadge patientId={patientId} />
              <PaymentBadge patientId={patientId} />
              <button
                type="button"
                className="cf360-mini-pill-btn"
                title="Open Periodontal Chart"
                onClick={() => setShowPerioChart(prev => !prev)}
                style={{ background: '#f0f9ff', color: '#0369a1', border: '1px solid #bae6fd', borderRadius: 12, padding: '2px 10px', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}
              >
                🦷 Perio
              </button>
              <button
                type="button"
                className="cf360-mini-pill-btn"
                title="Print Consent Form"
                onClick={() => setShowConsentForm(prev => !prev)}
                style={{ background: '#fdf4ff', color: '#7e22ce', border: '1px solid #e9d5ff', borderRadius: 12, padding: '2px 10px', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}
              >
                📋 Consent
              </button>
            </div>
          )}
          <div className="cf360-right-window-header">
            <button type="button" className="cf360-nav-btn" title="Previous">‹</button>
            <div className="cf360-panel-tabs">
              <button
                type="button"
                className={`cf360-panel-tab ${rightTab === 'patient-details' ? 'active' : ''}`}
                onClick={() => setRightTab('patient-details')}
              >
                Patient
              </button>
              <button
                type="button"
                className={`cf360-panel-tab ${rightTab === 'dental-notes' ? 'active' : ''}`}
                onClick={() => setRightTab('dental-notes')}
              >
                Dental Notes
              </button>
              <button
                type="button"
                className={`cf360-panel-tab ${rightTab === 'appointments' ? 'active' : ''}`}
                onClick={() => setRightTab('appointments')}
              >
                Appointments
              </button>
              <button
                type="button"
                className={`cf360-panel-tab ${rightTab === 'timeline' ? 'active' : ''}`}
                onClick={() => {
                  setRightTab('timeline');
                  setViewMode('timeline');
                }}
              >
                Timeline
              </button>
              <button
                type="button"
                className={`cf360-panel-tab ${rightTab === 'kanban' ? 'active' : ''}`}
                onClick={() => {
                  setRightTab('kanban');
                  setViewMode('kanban');
                }}
              >
                Kanban
              </button>
              <button
                type="button"
                className={`cf360-panel-tab ${rightTab === 'billing' ? 'active' : ''}`}
                onClick={() => setRightTab('billing')}
              >
                Billing
              </button>
              <button
                type="button"
                className={`cf360-panel-tab`}
                onClick={() => onOpenPharmacy?.()}
              >
                Pharmacy
              </button>
            </div>
            <button type="button" className="cf360-nav-btn" title="Next">›</button>
          </div>
          {rightTab === 'dental-notes' && (
            <>
            <ApexoToothChart
              visitId={visitId}
              patientId={patientId}
              onOpenBilling={() => {
                setRightTab('billing');
                onOpenBilling?.();
              }}
              onOpenDiagnosis={() => onOpenDiagnosis?.()}
              onOpenTreatment={() => onOpenTreatment?.()}
            />
            {/* Clinical Photo Gallery below chart */}
            {visitId && (
              <div style={{ padding: '8px 12px' }}>
                <PhotoGallery visitId={visitId} patientId={patientId} />
              </div>
            )}
            </>
          )}
          {rightTab === 'patient-details' && (
            <div className="cf360-modular-panel">
              <div className="cf360-modular-title">Patient Details</div>
              <div className="cf360-form-grid">
                <label>
                  Name
                  <input
                    value={String(patientDetails?.name || patientName || '')}
                    onChange={(e) =>
                      setPatientDetails((prev) => ({ ...(prev || { patient_id: patientId }), name: e.target.value }))
                    }
                  />
                </label>
                <label>
                  Mobile
                  <input
                    value={String(patientDetails?.mobile || patientDetails?.phone || '')}
                    onChange={(e) =>
                      setPatientDetails((prev) => ({ ...(prev || { patient_id: patientId }), mobile: e.target.value }))
                    }
                  />
                </label>
                <label>
                  Age
                  <input
                    type="number"
                    value={String(patientDetails?.age || '')}
                    onChange={(e) =>
                      setPatientDetails((prev) => ({ ...(prev || { patient_id: patientId }), age: e.target.value }))
                    }
                  />
                </label>
                <label>
                  Gender
                  <select
                    value={String(patientDetails?.gender || '')}
                    onChange={(e) =>
                      setPatientDetails((prev) => ({ ...(prev || { patient_id: patientId }), gender: e.target.value }))
                    }
                  >
                    <option value="">Select</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </label>
                <label>
                  Area
                  <input
                    value={String(patientDetails?.area || '')}
                    onChange={(e) =>
                      setPatientDetails((prev) => ({ ...(prev || { patient_id: patientId }), area: e.target.value }))
                    }
                  />
                </label>
                <label>
                  Reference
                  <input
                    value={String(patientDetails?.reference_source || '')}
                    onChange={(e) =>
                      setPatientDetails((prev) => ({
                        ...(prev || { patient_id: patientId }),
                        reference_source: e.target.value,
                      }))
                    }
                  />
                </label>
                <label className="full">
                  Medical Alerts
                  <textarea
                    value={String(patientDetails?.medical_alerts || '')}
                    onChange={(e) =>
                      setPatientDetails((prev) => ({
                        ...(prev || { patient_id: patientId }),
                        medical_alerts: e.target.value,
                      }))
                    }
                  />
                </label>
              </div>
              <div className="cf360-modular-actions">
                <button type="button" className="cf360-footer-btn" onClick={handleSavePatientDetails} disabled={detailsSaving}>
                  {detailsSaving ? 'Saving…' : 'Save Details'}
                </button>
              </div>

              {/* Clinical Tools Section */}
              <div className="cf360-modular-title" style={{ marginTop: 16 }}>Clinical Tools</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', padding: '0 0 8px' }}>
                <button type="button" className="cf360-footer-btn" style={{ fontSize: 12, padding: '6px 12px' }} onClick={() => setShowMedicalHistory(true)}>
                  📋 Medical History
                </button>
                <button type="button" className="cf360-footer-btn" style={{ fontSize: 12, padding: '6px 12px' }} onClick={() => setShowPerioChart(true)}>
                  🦷 Perio Chart
                </button>
                <button type="button" className="cf360-footer-btn" style={{ fontSize: 12, padding: '6px 12px' }} onClick={() => setShowConsentForm(true)}>
                  📝 Consent Form
                </button>
              </div>
            </div>
          )}
          {rightTab === 'appointments' && (
            <div className="cf360-modular-panel">
              <div className="cf360-modular-title">Next Appointment</div>
              <div className="cf360-form-grid compact">
                <label>
                  Date
                  <input type="date" value={appointmentDate} onChange={(e) => setAppointmentDate(e.target.value)} />
                </label>
                <label>
                  Time
                  <input type="time" value={appointmentTime} onChange={(e) => setAppointmentTime(e.target.value)} />
                </label>
                <label className="full">
                  Note
                  <input value={appointmentRemarks} onChange={(e) => setAppointmentRemarks(e.target.value)} />
                </label>
              </div>
              <div className="cf360-modular-actions">
                <button type="button" className="cf360-footer-btn" onClick={handleQuickSchedule} disabled={appointmentSaving}>
                  {appointmentSaving ? 'Scheduling…' : 'Schedule'}
                </button>
              </div>

              <div className="cf360-modular-title">Past History</div>
              <div className="cf360-history-list">
                {patientVisits.length === 0 && <div className="cf360-empty-mini">No visits yet.</div>}
                {patientVisits.slice(0, 12).map((visit) => (
                  <div key={visit.visit_id} className="cf360-history-item">
                    <div>
                      <strong>#{visit.visit_id}</strong> {visit.visit_type || 'Visit'}
                      <div className="meta">
                        {visit.visit_date || '—'} {visit.appointment_time ? `• ${new Date(visit.appointment_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : ''}
                      </div>
                    </div>
                    <span className="status">{visit.status || 'Open'}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {rightTab === 'billing' && (
            <div className="cf360-modular-panel">
              <div className="cf360-modular-title">Billing Snapshot</div>
              <div className="cf360-kpi-grid">
                <div className="kpi-card">
                  <span>Items</span>
                  <strong>{billingSnapshot.treatmentItems}</strong>
                </div>
                <div className="kpi-card">
                  <span>Est. Amount</span>
                  <strong>₹{billingSnapshot.estAmount.toLocaleString('en-IN')}</strong>
                </div>
                <div className="kpi-card">
                  <span>Consultant</span>
                  <strong>₹{billingSnapshot.consultantAmount.toLocaleString('en-IN')}</strong>
                </div>
              </div>
              <div className="cf360-empty-mini">{billingSnapshot.pendingLabel}</div>
              <div className="cf360-modular-actions">
                <button
                  type="button"
                  className="cf360-footer-btn"
                  onClick={() => onOpenBilling?.()}
                >
                  Open Full Billing
                </button>
              </div>
            </div>
          )}
          {rightTab === 'timeline' && (
            <div className="cf360-modular-panel">
              <div className="cf360-empty-mini">Timeline is on the center panel for faster review.</div>
            </div>
          )}
          {rightTab === 'kanban' && (
            <div className="cf360-modular-panel">
              <div className="cf360-empty-mini">Kanban board is on the center panel for large view.</div>
            </div>
          )}
          <div className="cf360-panel-footer">
            <button type="button" className="cf360-footer-btn" onClick={handleManualSave}>
              {pendingDeletes.length > 0 || pendingSaves.length > 0 ? `💾 Save Chart (${pendingDeletes.length + pendingSaves.length})` : 'Save'}
            </button>
            <button type="button" className="cf360-footer-btn" onClick={() => onClosePanel?.()}>
              Close
            </button>
          </div>
        </div>
      </div>

      {/* === Clinical Tool Modals (self-contained overlays) === */}
      {showMedicalHistory && patientId && (
        <MedicalHistoryForm patientId={patientId} onClose={() => setShowMedicalHistory(false)} />
      )}

      {showPerioChart && patientId && (
        <PerioChart patientId={patientId} onClose={() => setShowPerioChart(false)} />
      )}

      {showConsentForm && visitId && (
        <ConsentForm visitId={visitId} patientId={patientId} onClose={() => setShowConsentForm(false)} />
      )}
    </div>
  );
}
